import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { useEffect, useMemo, useState } from "react";
import useGetDailyFormVehicles from "./useGetDailyFormVehicles";
import useDeleteDailyFormVehicle from "./useDeleteDailyFormVehicle";
import useModalState from "hooks/useModalState";
import { IconButton, Icon, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Link } from "react-router-dom";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import dayjs from "dayjs";

const getNextServiceDueLabel = (vehicle: any) => {
  const activePlans = (vehicle.servicePlans || []).filter((plan) => plan.isActive);

  if (!activePlans.length) {
    return "No plans";
  }

  let byDate: any = null;
  let byMeter: any = null;

  activePlans.forEach((plan) => {
    if (plan.nextDueDate) {
      if (!byDate || dayjs(plan.nextDueDate).valueOf() < dayjs(byDate.nextDueDate).valueOf()) {
        byDate = plan;
      }
    }

    if (plan.nextDueMeterReading !== null && plan.nextDueMeterReading !== undefined) {
      if (!byMeter || plan.nextDueMeterReading < byMeter.nextDueMeterReading) {
        byMeter = plan;
      }
    }
  });

  if (byDate) {
    return `${byDate.name}: ${dayjs(byDate.nextDueDate).format("MM/DD/YYYY")}`;
  }

  if (byMeter) {
    return `${byMeter.name}: ${Number(byMeter.nextDueMeterReading).toLocaleString()}`;
  }

  return "No due date";
};

export default function useVehiclesTable() {
  const { offset } = usePaginatedVariables();
  const [getDailyFormVehicles, { data, loading, error, paginatorInfo }] = useGetDailyFormVehicles();

  const [deleteVehicle] = useDeleteDailyFormVehicle();
  const {
    open: deleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useModalState();
  const [vehicleToDelete, setVehicleToDelete] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    getDailyFormVehicles({ first: 1000, page: 1 });
  }, [getDailyFormVehicles]);

  const pagination = usePaginatedResources({
    paginatorInfo,
    paginate: offset.paginate,
  });

  const handleDeleteClick = (vehicleId: string, vehicleName: string) => {
    setVehicleToDelete({ id: vehicleId, name: vehicleName });
    openDeleteDialog();
  };

  const handleDeleteConfirm = async () => {
    if (vehicleToDelete) {
      const vehicleId = vehicleToDelete.id;
      closeDeleteDialog();
      setVehicleToDelete(null);

      await deleteVehicle({
        variables: { id: vehicleId },
      });
    }
  };

  const handleDeleteCancel = () => {
    closeDeleteDialog();
    setVehicleToDelete(null);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ value, row }: any) => (
          <MDTypography
            variant="body"
            color="info"
            sx={{
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            component={Link}
            to={`/fleet/vehicles/${row.original.id}/edit`}
          >
            {value}
          </MDTypography>
        ),
      },
      { Header: "Type", accessor: "type" },
      { Header: "Make / Model", accessor: "makeModel" },
      { Header: "Plate", accessor: "licensePlate" },
      { Header: "Current Meter", accessor: "currentMeterReading" },
      { Header: "Registration Exp.", accessor: "registrationExpirationDate" },
      { Header: "Next Service Due", accessor: "nextServiceDue" },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <IconButton
              component={Link}
              to={`/fleet/vehicles/${row.original.id}/edit`}
              size="small"
              color="info"
            >
              <Icon>edit</Icon>
            </IconButton>
            <IconButton
              onClick={() => handleDeleteClick(row.original.id, row.original.name)}
              size="small"
              color="error"
            >
              <Icon>delete</Icon>
            </IconButton>
          </div>
        ),
      },
    ];
  }, []);

  const rows = useMemo(() => {
    return (
      (data as any[] | undefined)?.map((vehicle) => ({
        id: vehicle.id,
        name: vehicle.name,
        type: vehicle.dailyFormVehicleType?.name || "N/A",
        makeModel: [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") || "N/A",
        licensePlate: vehicle.licensePlate || "N/A",
        currentMeterReading:
          vehicle.currentMeterReading !== null && vehicle.currentMeterReading !== undefined
            ? `${Number(vehicle.currentMeterReading).toLocaleString()} ${vehicle.meterType || ""}`.trim()
            : "N/A",
        registrationExpirationDate: vehicle.registrationExpirationDate
          ? dayjs(vehicle.registrationExpirationDate).format("MM/DD/YYYY")
          : "N/A",
        nextServiceDue: getNextServiceDueLabel(vehicle),
      })) || []
    );
  }, [data]);

  const DeleteConfirmationDialog = () => (
    <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
      <DialogTitle>Delete Vehicle</DialogTitle>
      <DialogContent>
        <MDTypography variant="body2">
          Are you sure you want to delete &ldquo;{vehicleToDelete?.name}&rdquo;? This action cannot
          be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleDeleteCancel} color="secondary">
          Cancel
        </MDButton>
        <MDButton onClick={handleDeleteConfirm} color="error">
          Delete
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  return {
    columns,
    rows,
    loading,
    error,
    pagination,
    DeleteConfirmationDialog,
  };
}
